<!-- SIM卡列表页面 - 显示SIM卡详细信息，支持搜索、过滤和分页 -->
<template>
  <div class="sim-card-list">
    <!-- 搜索和过滤 -->
    <div class="search-section">
      <el-card>
        <template #header>
          <div class="search-header">
            <span>搜索条件</span>
            <!-- 使用 link 属性替代 type="text"，符合 Element Plus 3.0 规范 -->
            <el-button link @click="toggleSearchForm">
              {{ showSearchForm ? "收起" : "展开" }}
              <el-icon
                ><ArrowDown v-if="!showSearchForm" /><ArrowUp v-else
              /></el-icon>
            </el-button>
          </div>
        </template>

        <div v-show="showSearchForm" class="search-form">
          <el-row :gutter="20">
            <el-col :span="8">
              <el-form-item label="IMSI">
                <el-input
                  v-model="searchForm.imsi"
                  placeholder="请输入完整卡号"
                  clearable
                  @keyup.enter="handleSearch"
                />
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item>
                <el-button
                  type="primary"
                  @click="handleSearch"
                  :loading="loading"
                >
                  <el-icon><Search /></el-icon>
                  查询
                </el-button>
                <el-button @click="handleReset">
                  <el-icon><Refresh /></el-icon>
                  重置
                </el-button>
              </el-form-item>
            </el-col>
          </el-row>
        </div>
      </el-card>
    </div>

    <!-- 标签页和操作按钮 -->
    <div class="tabs-section">
      <el-card>
        <div class="tabs-header">
          <el-tabs v-model="activeTab" @tab-change="handleTabChange">
            <el-tab-pane label="所有卡" name="all">
              <template #label> 所有卡 </template>
            </el-tab-pane>
            <el-tab-pane label="超限卡" name="overLimit">
              <template #label> 超限卡 </template>
            </el-tab-pane>
            <el-tab-pane label="流量池统计" name="poolStats">
              <template #label> 流量池统计 </template>
            </el-tab-pane>
          </el-tabs>

          <div class="tabs-actions" v-if="isSuperAdmin">
            <el-button type="primary" @click="handleExport">
              <el-icon><Download /></el-icon>
              导出数据
            </el-button>
            <el-button @click="handleBatchOperation">
              <el-icon><Operation /></el-icon>
              批量操作
            </el-button>
            <el-button @click="handleAdvancedExport">
              <el-icon><Document /></el-icon>
              高级导出
            </el-button>
            <el-button @click="handleSettings">
              <el-icon><Setting /></el-icon>
            </el-button>
          </div>
        </div>
      </el-card>
    </div>

    <!-- SIM卡列表 -->
    <div class="list-section" v-if="activeTab !== 'poolStats'">
      <el-card v-loading="loading">
        <el-table
          ref="simTableRef"
          :data="simCards"
          stripe
          style="width: 100%"
          @selection-change="handleSelectionChange"
        >
          <el-table-column type="expand">
            <template #default="{ row }">
              <SimCardDetail :card-id="row.iccid || row.imsi" />
            </template>
          </el-table-column>
          <el-table-column type="selection" width="55" v-if="isSuperAdmin" />
          <el-table-column label="单卡详情" width="120">
            <template #default="{ row }">
              <el-button link type="primary" @click="toggleRowExpansion(row)">
                点击展开
              </el-button>
            </template>
          </el-table-column>
          <el-table-column prop="imsi" label="IMSI" width="180">
            <template #default="{ row }">
              <el-link type="primary" @click="goToDevice(row.imsi)">{{
                row.imsi
              }}</el-link>
            </template>
          </el-table-column>
          <el-table-column prop="iccid" label="ICCID" width="200" />
          <el-table-column prop="operator" label="运营商" width="100" />
          <el-table-column prop="package" label="套餐" width="150" />
          <el-table-column prop="status" label="卡状态" width="100">
            <template #default="{ row }">
              <el-tag :type="getStatusType(row.status)" size="small">
                {{ row.status }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="activationDate" label="激活日期" width="120" />
          <el-table-column prop="expirationDate" label="到期日期" width="120" />
          <el-table-column prop="currentUsage" label="用量" width="180">
            <template #default="{ row }">
              <div>{{ row.currentUsage }}</div>
              <el-progress
                :percentage="row.usagePercentage"
                :status="row.usagePercentage > 90 ? 'exception' : ''"
              />
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-section">
          <el-pagination
            v-model:current-page="currentPage"
            v-model:page-size="pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="totalCards"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </el-card>
    </div>

    <!-- 流量池统计列表 -->
    <div class="list-section" v-else>
      <el-card v-loading="loading">
        <el-table :data="billingGroups" stripe style="width: 100%">
          <el-table-column prop="code" label="计费组代码" width="200" />
          <el-table-column prop="name" label="名称" width="200" />
          <el-table-column prop="trafficPoolName" label="流量池" width="200" />
          <el-table-column prop="billingMode" label="计费模式" width="100" />
          <el-table-column prop="availableCards" label="可用卡数" width="100" />
          <el-table-column prop="activatedCards" label="激活卡数" width="100" />
          <el-table-column
            prop="totalTrafficMB"
            label="总流量(MB)"
            width="150"
          />
          <el-table-column
            prop="usedTrafficMB"
            label="已用流量(MB)"
            width="150"
          />
          <el-table-column label="使用率" width="150">
            <template #default="{ row }">
              <el-progress :percentage="row.usedTrafficPercentage" />
            </template>
          </el-table-column>
        </el-table>
      </el-card>
    </div>

    <!-- 诊断对话框 -->
    <el-dialog v-model="diagnosisDialogVisible" title="SIM卡诊断" width="600px">
      <div v-if="selectedCard">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="ICCID">{{
            selectedCard.iccid
          }}</el-descriptions-item>
          <el-descriptions-item label="MSISDN">{{
            selectedCard.msisdn
          }}</el-descriptions-item>
          <el-descriptions-item label="运营商">{{
            selectedCard.operator
          }}</el-descriptions-item>
          <el-descriptions-item label="状态">{{
            selectedCard.status
          }}</el-descriptions-item>
          <el-descriptions-item label="套餐">{{
            selectedCard.package
          }}</el-descriptions-item>
          <el-descriptions-item label="流量使用">{{
            selectedCard.currentUsage
          }}</el-descriptions-item>
        </el-descriptions>

        <div class="diagnosis-result">
          <h4>诊断结果</h4>
          <el-alert
            v-if="diagnosisResult"
            :title="diagnosisResult.title"
            :type="diagnosisResult.type"
            :description="diagnosisResult.description"
            show-icon
          />
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import { ElMessage, ElMessageBox } from "element-plus";
import * as ElementPlusIconsVue from "@element-plus/icons-vue";
const {
  Refresh,
  Search,
  Plus,
  Download,
  Operation,
  Document,
  Setting,
  ArrowDown,
  ArrowUp,
} = ElementPlusIconsVue as any;
import apiClient from "@/services/apiClient";
import { API_URLS } from "@/config";
import { useStore } from "vuex";
import SimCardDetail from "./SimCardDetail.vue";

// 路由
const router = useRouter();
const store = useStore();

// 检查是否为超级管理员
const isSuperAdmin = computed(() => {
  return store.state.user?.role === "super_admin";
});

// 类型定义
interface SimCard {
  msisdn: string;
  billingGroup: string;
  iccid: string;
  imsi: string;
  operator: string;
  package: string;
  status: string;
  activationDate: string;
  expirationDate: string;
  currentUsage: string;
  usagePercentage: number;
  overLimitMB: number;
  billingGroupStats?: string;
}

interface SearchForm {
  msisdn: string;
  iccid: string;
  imsi: string;
  billingGroup: string;
  operator: string;
  overPackage: string;
}

// 响应式数据
const loading = ref(false);
const showSearchForm = ref(false);
const activeTab = ref("all");
const currentPage = ref(1);
const pageSize = ref(10);
const totalCards = ref(0);
const overLimitCount = ref(0);
const expiringCount = ref(0);
const simCards = ref<SimCard[]>([]);
const selectedCards = ref<SimCard[]>([]);
const diagnosisDialogVisible = ref(false);
const selectedCard = ref<SimCard | null>(null);
const diagnosisResult = ref<any>(null);
const billingGroups = ref<any[]>([]);
const simTableRef = ref<any>(null);

// 搜索表单
const searchForm = ref<any>({
  imsi: "",
});

// 方法
const fetchBillingGroups = async () => {
  try {
    const response = await apiClient.get(API_URLS.SIM_CARDS_BILLING_GROUPS);

    if (response.data.code === 0) {
      billingGroups.value = response.data.data.billingGroups || [];
    }
  } catch (error) {
    console.error("获取计费组数据失败:", error);
  }
};

const fetchSimCards = async () => {
  try {
    loading.value = true;
    const params: any = {
      page: currentPage.value,
      size: pageSize.value,
      imsi: searchForm.value.imsi || undefined,
    };

    // 如果在超限卡标签，增加过滤
    if (activeTab.value === "overLimit") {
      params.over_package = "1";
    }

    const response = await apiClient.get(API_URLS.SIM_CARDS_LIST, { params });

    if (response.data.code === 0) {
      const data = response.data.data;
      simCards.value = data.simCards || [];
      totalCards.value = data.total || 0;
    } else {
      ElMessage.error(response.data.message || "获取SIM卡列表失败");
    }
  } catch (error) {
    console.error("获取SIM卡列表失败:", error);
    ElMessage.error("获取SIM卡列表失败");
  } finally {
    loading.value = false;
  }
};

const fetchPoolStats = async () => {
  try {
    loading.value = true;
    const response = await apiClient.get(API_URLS.SIM_CARDS_BILLING_GROUPS);
    if (response.data.code === 0) {
      billingGroups.value = response.data.data.billingGroups || [];
    }
  } catch (error) {
    console.error("获取流量池统计失败:", error);
  } finally {
    loading.value = false;
  }
};

const handleTabChange = (name: any) => {
  const tabName = typeof name === "string" ? name : name.props.name;
  activeTab.value = tabName;
  currentPage.value = 1;
  if (tabName === "poolStats") {
    fetchPoolStats();
  } else {
    fetchSimCards();
  }
};

const handleSearch = () => {
  currentPage.value = 1;
  fetchSimCards();
};

const handleReset = () => {
  searchForm.value = {
    imsi: "",
  };
  currentPage.value = 1;
  fetchSimCards();
};

const goToDevice = (imsi: string) => {
  router.push({
    path: "/deviceManagement",
    query: { imsi },
  });
};

const toggleRowExpansion = (row: any) => {
  if (simTableRef.value) {
    simTableRef.value.toggleRowExpansion(row);
  }
};

const handleSizeChange = (size: number) => {
  pageSize.value = size;
  currentPage.value = 1;
  fetchSimCards();
};

const handleCurrentChange = (page: number) => {
  currentPage.value = page;
  fetchSimCards();
};

const handleSelectionChange = (selection: SimCard[]) => {
  selectedCards.value = selection;
};

const handleDiagnosis = async (card: SimCard) => {
  selectedCard.value = card;
  diagnosisDialogVisible.value = true;

  try {
    // 这里可以调用诊断API
    diagnosisResult.value = {
      title: "诊断完成",
      type: "success",
      description: "SIM卡状态正常，网络连接良好",
    };
  } catch (error) {
    diagnosisResult.value = {
      title: "诊断失败",
      type: "error",
      description: "无法连接到诊断服务",
    };
  }
};

const handleExport = async () => {
  try {
    ElMessage.info("导出功能开发中...");
  } catch (error) {
    ElMessage.error("导出失败");
  }
};

const handleBatchOperation = async () => {
  if (selectedCards.value.length === 0) {
    ElMessage.warning("请先选择要操作的SIM卡");
    return;
  }

  try {
    await ElMessageBox.confirm(
      `确定要对选中的 ${selectedCards.value.length} 张SIM卡进行批量操作吗？`,
      "批量操作",
      {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      }
    );

    ElMessage.info("批量操作功能开发中...");
  } catch (error) {
    // 用户取消
  }
};

const handleAdvancedExport = () => {
  ElMessage.info("高级导出功能开发中...");
};

const handleSettings = () => {
  ElMessage.info("设置功能开发中...");
};

const toggleSearchForm = () => {
  showSearchForm.value = !showSearchForm.value;
};

const getStatusType = (
  status: string
): "success" | "primary" | "danger" | "warning" | "info" => {
  const statusMap: Record<
    string,
    "success" | "primary" | "danger" | "warning" | "info"
  > = {
    正常: "success",
    测试期: "primary",
    停机: "danger",
    沉默期: "warning",
  };
  return statusMap[status] || "info";
};

const refreshData = () => {
  fetchSimCards();
};

const handleBillingGroupChange = (card: SimCard) => {
  // 处理计费组统计变化
  console.log("计费组统计变化:", card.iccid, card.billingGroupStats);
  ElMessage.info(`已更新 ${card.iccid} 的计费组统计`);
};

const goToSimCardStats = () => {
  router.push("/simCardStats");
};

// 生命周期
onMounted(() => {
  fetchBillingGroups();
  fetchSimCards();
});
</script>

<style scoped>
.sim-card-list {
  padding: 20px;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.list-header h2 {
  margin: 0;
  color: #303133;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.search-section,
.tabs-section,
.list-section {
  margin-bottom: 20px;
}

.search-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-form {
  padding-top: 10px;
}

.tabs-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.tabs-actions {
  display: flex;
  gap: 10px;
}

.pagination-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
}

.pagination-info {
  color: #606266;
  font-size: 14px;
}

.diagnosis-result {
  margin-top: 20px;
}

.diagnosis-result h4 {
  margin-bottom: 10px;
  color: #303133;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .list-header {
    flex-direction: column;
    gap: 15px;
  }

  .header-actions {
    width: 100%;
    justify-content: center;
  }

  .tabs-header {
    flex-direction: column;
    gap: 15px;
  }

  .tabs-actions {
    width: 100%;
    justify-content: center;
  }

  .pagination-section {
    flex-direction: column;
    gap: 10px;
  }
}
</style>
